import { CommonMessage } from '@frontend/lang';
import { ProductClient } from '@frontend/product/api';
import { IProduct } from '@frontend/product/types';
import { RequirementClient, StockExtendedClient } from '@frontend/stock/api';
import { Requirement, StockExtended } from '@frontend/stock/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { openMultipleSlots, openSlot } from '../../utils/slot-opener';

export type StockType = 'consumable' | 'reusable';

export interface UseStockDetailOptions {
    stockType: StockType;
}

export interface StockLocation {
    id: string;
    accountId: string;
    spotId: string;
    moduleId: string;
    slotId: string;
    slotName: string;
    quantity: number;
    stocks: StockExtended[];
}

const getSlotName = (stock: StockExtended): string => {
    const slotData = stock.slot?.data;
    return stock.slot?.name ?? slotData?.display ?? slotData?.number ?? stock.slot_id;
};

export const useStockDetail = (options: UseStockDetailOptions) => {
    const { stockType } = options;
    const navigate = useKioskNavigate();
    const { productId } = useParams();
    const navigationState = useAppSelector((store) => store.navigation);
    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const spotId = navigationState.spot?.id;
    const [product, setProduct] = useState<IProduct | null>(null);
    const [stocks, setStocks] = useState<StockExtended[]>([]);
    const [requirement, setRequirement] = useState<Requirement | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<React.ReactNode | null>(null);
    const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set());
    const [isOpeningSlots, setIsOpeningSlots] = useState(false);

    useEffect(() => {
        if (!productId || !accountId) {
            setIsLoading(false);
            if (!productId) setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.PRODUCT.DEFAULT));
            return;
        }

        const fetchData = async () => {
            try {
                const [productData, stockData, requirementData] = await Promise.all([
                    ProductClient.fetchProduct(accountId, productId),
                    stockType === 'consumable'
                        ? StockExtendedClient.fetchConsumableStocksExtended({
                              account_id: accountId,
                              spot_id: spotId,
                              product_id: productId,
                              index: '0',
                              size: '100'
                          })
                        : StockExtendedClient.fetchReusableStocksExtended({
                              account_id: accountId,
                              spot_id: spotId,
                              product_id: productId,
                              index: '0',
                              size: '100'
                          }),
                    RequirementClient.fetchRequirements({
                        account_id: accountId,
                        spot_id: spotId,
                        product_id: productId
                    })
                ]);

                setProduct(productData);
                setStocks(stockData.results);
                setSelectedLocationIds(new Set());
                setRequirement(requirementData.results[0] ?? null);
            } catch {
                setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.PRODUCT.DEFAULT));
                setProduct(null);
                setStocks([]);
                setSelectedLocationIds(new Set());
                setRequirement(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [productId, accountId, spotId, stockType]);

    const stockLocations = useMemo<StockLocation[]>(() => {
        const locations = new Map<string, StockLocation>();

        for (const stock of stocks) {
            const existing = locations.get(stock.slot_id);
            if (existing) {
                existing.quantity += stock.quantity;
                existing.stocks.push(stock);
                continue;
            }

            locations.set(stock.slot_id, {
                id: stock.slot_id,
                accountId: stock.account_id,
                spotId: stock.spot_id,
                moduleId: stock.module_id,
                slotId: stock.slot_id,
                slotName: getSlotName(stock),
                quantity: stock.quantity,
                stocks: [stock]
            });
        }

        return [...locations.values()].sort((a, b) => a.slotName.localeCompare(b.slotName, undefined, { numeric: true, sensitivity: 'base' }));
    }, [stocks]);

    const selectedLocations = useMemo(() => stockLocations.filter((location) => selectedLocationIds.has(location.id)), [stockLocations, selectedLocationIds]);

    const toggleLocation = useCallback((locationId: string) => {
        setSelectedLocationIds((prev) => {
            const next = new Set(prev);
            if (next.has(locationId)) next.delete(locationId);
            else next.add(locationId);
            return next;
        });
    }, []);

    const selectAllLocations = useCallback(() => {
        setSelectedLocationIds(new Set(stockLocations.map((location) => location.id)));
    }, [stockLocations]);

    const openSelectedSlots = useCallback(async () => {
        if (selectedLocations.length === 0) return;

        const slots = selectedLocations.map((location) => ({
            accountId: location.accountId,
            spotId: location.spotId,
            moduleId: location.moduleId,
            slotId: location.slotId,
            iotId: navigationState.iot?.id
        }));

        setIsOpeningSlots(true);
        try {
            if (slots.length === 1) await openSlot(slots[0]);
            else await openMultipleSlots(slots);
        } finally {
            setIsOpeningSlots(false);
        }
    }, [navigationState.iot?.id, selectedLocations]);

    return {
        navigate,
        product,
        stocks,
        stockLocations,
        selectedLocationIds,
        selectedLocations,
        selectedLocationCount: selectedLocations.length,
        toggleLocation,
        selectAllLocations,
        openSelectedSlots,
        isOpeningSlots,
        requirement,
        totalQuantity: stocks.reduce((sum, s) => sum + s.quantity, 0),
        isLoading,
        error,
        hasError: !!error
    };
};
